/**
 * Turns a generated document's markdown into a real file the user can hand
 * to someone else — PDF, Word, and plain Markdown.
 *
 * `generate_document` used to return markdown that only the chat's Download
 * glyph touched, and that glyph wrote a `.md` blob and nothing else — no PDF,
 * no Word doc, despite `file-group.tsx` showing rows for both. This module
 * makes those rows real.
 *
 * `jspdf` and `docx` are both dynamically imported inside `exportPdf` /
 * `exportDocx` so neither library sits in the initial bundle — only a click
 * on a PDF or Word row pulls them in.
 */

import type { PdfRenderOptions } from './pdf-fonts';

/** Details a publisher needs, deliberately kept outside the article body. */
export interface DocumentPublishingMetadata {
  metaTitle?: string | null;
  metaDescription?: string | null;
  slug?: string | null;
}

function publishingRows(metadata: DocumentPublishingMetadata): Array<[string, string]> {
  return [
    ['Title tag', metadata.metaTitle?.trim() ?? ''],
    ['Meta description', metadata.metaDescription?.trim() ?? ''],
    ['Slug', metadata.slug?.trim() ? `/${metadata.slug.trim().replace(/^\/+/, '')}` : ''],
  ].filter(([, value]) => Boolean(value));
}

/** CMS-safe front matter for Markdown handoff; omitted for ordinary documents. */
export function markdownWithPublishingMetadata(
  markdown: string,
  metadata?: DocumentPublishingMetadata,
): string {
  if (!metadata) return markdown;
  const rows = publishingRows(metadata);
  if (!rows.length) return markdown;
  const fieldName: Record<string, string> = {
    'Title tag': 'meta_title',
    'Meta description': 'meta_description',
    Slug: 'slug',
  };
  const frontMatter = rows
    .map(([label, value]) => `${fieldName[label]}: ${JSON.stringify(value)}`)
    .join('\n');
  return `---\n${frontMatter}\n---\n\n${markdown}`;
}

/** A clearly separated appendix for Word/PDF handoff, never embedded in the article itself. */
export function markdownWithPublishingAppendix(
  markdown: string,
  metadata?: DocumentPublishingMetadata,
): string {
  if (!metadata) return markdown;
  const rows = publishingRows(metadata);
  if (!rows.length) return markdown;
  return `${markdown.trimEnd()}\n\n---\n\n## CMS publishing details\n\n| Field | Value |\n| --- | --- |\n${rows.map(([label, value]) => `| ${label} | ${value.replaceAll('|', '\\|')} |`).join('\n')}\n`;
}

/** Slugified filename base, e.g. "Q3 Growth Brief" -> "q3-growth-brief". */
export function slugifyFilename(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'document';
}

const DOCUMENT_FILENAME_MAX_BASE_LENGTH = 120;

/**
 * Title-cases a single word, preserving capitalisation the word already
 * has. "MBA" must stay "MBA", not become "Mba" — so a word whose first
 * character is already uppercase is left untouched; only a word that
 * starts lowercase gets its first character upper-cased.
 */
function titleCaseWord(word: string): string {
  if (!word) return word;
  const first = word.charAt(0);
  const alreadyUpper = first.toLowerCase() !== first;
  if (alreadyUpper) return word;
  return first.toUpperCase() + word.slice(1);
}

/**
 * Founder requirement (verbatim): "I want you to always append every
 * document, if it's downloaded... with _Mora at the end". Reference is
 * ChatGPT's download naming — Title_Case_Words_Joined_By_Underscores, e.g.
 * `Pit_Stop_Website_Design_Development_Agreement_Mora.pdf` — not the
 * lowercase-hyphen slug `slugifyFilename` produces, which reads as a
 * filesystem artifact rather than a document a person was handed.
 *
 * Every non-alphanumeric run (whitespace, punctuation, illegal filename
 * characters alike) collapses to a single `_` separator — nothing but
 * `[A-Za-z0-9_]` plus the extension ever survives, so there is no need to
 * special-case individual illegal characters. Words that are already
 * capitalised (acronyms like "MBA") are preserved rather than mangled;
 * words that are not get Title-Cased.
 */
export function documentFilename(title: string, extension: string): string {
  const rawWords = (title ?? '').split(/[^A-Za-z0-9]+/).filter(Boolean);
  const words = rawWords.length > 0 ? rawWords.map(titleCaseWord) : ['Document'];

  const capped: string[] = [];
  let length = 0;
  for (const word of words) {
    const additional = (capped.length > 0 ? 1 : 0) + word.length;
    if (capped.length > 0 && length + additional > DOCUMENT_FILENAME_MAX_BASE_LENGTH) break;
    capped.push(word);
    length += additional;
  }

  const lastWord = capped[capped.length - 1];
  if (lastWord && lastWord.toLowerCase() === 'mora') {
    // Title already ends in "Mora" — normalise casing rather than doubling
    // it into "..._Mora_Mora".
    capped[capped.length - 1] = 'Mora';
  } else {
    capped.push('Mora');
  }

  const base = capped.join('_');
  const cleanExtension = extension.replace(/^\.+/, '');
  return `${base}.${cleanExtension}`;
}

/** Shared "make a blob, click an anchor, revoke the URL" download dance. */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportMarkdown(
  markdown: string,
  title: string,
  metadata?: DocumentPublishingMetadata,
): void {
  const blob = new Blob([markdownWithPublishingMetadata(markdown, metadata)], {
    type: 'text/markdown',
  });
  triggerBlobDownload(blob, documentFilename(title, 'md'));
}

/** Bytes shared by preview and download, preserving the same recursive document model. */
export async function buildPdfBlob(
  markdown: string,
  title: string,
  options?: PdfRenderOptions,
  metadata?: DocumentPublishingMetadata,
): Promise<Blob> {
  const [{ renderPdf }, { parseDocument }] = await Promise.all([
    import('./render-pdf'),
    import('./markdown-document'),
  ]);
  return renderPdf(
    parseDocument(markdownWithPublishingAppendix(markdown, metadata), title),
    title,
    options,
  );
}

export async function exportPdf(
  markdown: string,
  title: string,
  metadata?: DocumentPublishingMetadata,
): Promise<void> {
  triggerBlobDownload(
    await buildPdfBlob(markdown, title, undefined, metadata),
    documentFilename(title, 'pdf'),
  );
}

export async function buildDocxBlob(
  markdown: string,
  title: string,
  metadata?: DocumentPublishingMetadata,
): Promise<Blob> {
  const [{ renderDocx }, { parseDocument }] = await Promise.all([
    import('./render-docx'),
    import('./markdown-document'),
  ]);
  return renderDocx(
    parseDocument(markdownWithPublishingAppendix(markdown, metadata), title),
    title,
  );
}

export async function exportDocx(
  markdown: string,
  title: string,
  metadata?: DocumentPublishingMetadata,
): Promise<void> {
  triggerBlobDownload(
    await buildDocxBlob(markdown, title, metadata),
    documentFilename(title, 'docx'),
  );
}
